import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../AuthContext';
import api from '../api/axios';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Tabs,
  Tab,
  Avatar,
  Card,
  CardContent,
  Grid,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Pagination
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Assignment as AssignmentIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import BreadcrumbsNav from '../components/BreadcrumbsNav';

const UserManagementPage = () => {
  const { user: currentUser, canManageUsers, hasRole } = useContext(AuthContext);
  
  // State
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [projects, setProjects] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Filters
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modals
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Form data
  const [inviteForm, setInviteForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: 'associate',
    assignedCompanies: [],
    assignedProjects: [],
    invitationMessage: ''
  });

  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    role: '',
    isActive: true
  });

  const [assignForm, setAssignForm] = useState({
    type: 'company', // 'company' or 'project'
    companyId: '',
    projectId: '',
    permissions: {},
    sectionAccess: {}
  });

  const roleDisplayNames = {
    'super_admin': 'Super Admin',
    'admin': 'Admin',
    'manager': 'Manager',
    'associate': 'Associate'
  };

  const roleColors = {
    'super_admin': 'error',
    'admin': 'warning',
    'manager': 'info',
    'associate': 'success'
  };

  // Fetch data
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10'
      });
      
      if (roleFilter) params.append('role', roleFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (searchTerm) params.append('search', searchTerm);

      const response = await api.get(`/users?${params}`);
      setUsers(response.data.docs || []);
      setTotalPages(response.data.totalPages || 1);
    } catch (err) {
      setError('Failed to fetch users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/companies');
      setCompanies(response.data || []);
    } catch (err) {
      console.error('Failed to fetch companies:', err);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await api.get('/projects');
      setProjects(response.data || []);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    }
  };

  const fetchInvitations = async () => {
    try {
      const response = await api.get('/users/invitations/pending');
      setInvitations(response.data.invitations || []);
    } catch (err) {
      console.error('Failed to fetch invitations:', err);
    }
  };

  useEffect(() => {
    if (canManageUsers()) {
      fetchUsers();
      fetchCompanies();
      fetchProjects();
      fetchInvitations();
    }
  }, [page, roleFilter, statusFilter, searchTerm]);

  // Handlers
  const handleInviteUser = async () => {
    try {
      setError('');
      await api.post('/auth/invite', inviteForm);
      setInviteModalOpen(false);
      setInviteForm({
        email: '',
        firstName: '',
        lastName: '',
        role: 'associate',
        assignedCompanies: [],
        assignedProjects: [],
        invitationMessage: ''
      });
      fetchUsers();
      fetchInvitations();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send invitation');
    }
  };

  const handleEditUser = async () => {
    try {
      setError('');
      await api.put(`/users/${selectedUser._id}`, editForm);
      setEditModalOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update user');
    }
  };

  const handleDeactivateUser = async (userId) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) return;
    
    try {
      await api.delete(`/users/${userId}`);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to deactivate user');
    }
  };

  const handleAssignUser = async () => {
    try {
      setError('');
      const endpoint = assignForm.type === 'company' 
        ? `/users/${selectedUser._id}/assign-company`
        : `/users/${selectedUser._id}/assign-project`;
      
      const data = assignForm.type === 'company'
        ? { companyId: assignForm.companyId, permissions: assignForm.permissions }
        : { 
            projectId: assignForm.projectId, 
            companyId: assignForm.companyId,
            sectionAccess: assignForm.sectionAccess 
          };

      await api.post(endpoint, data);
      setAssignModalOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to assign user');
    }
  };

  const handleResendInvitation = async (invitationId) => {
    try {
      await api.post(`/users/invitations/${invitationId}/resend`);
      fetchInvitations();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend invitation');
    }
  };

  const handleRevokeInvitation = async (invitationId) => {
    if (!window.confirm('Are you sure you want to revoke this invitation?')) return;
    
    try {
      await api.delete(`/users/invitations/${invitationId}`);
      fetchInvitations();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to revoke invitation');
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setEditForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      role: user.role,
      isActive: user.isActive
    });
    setEditModalOpen(true);
  };

  const openAssignModal = (user) => {
    setSelectedUser(user);
    setAssignForm({
      type: 'company',
      companyId: '',
      projectId: '',
      permissions: {
        canManageProjects: false,
        canViewAnalytics: true,
        canManageBilling: false
      },
      sectionAccess: {
        flowDesigner: false,
        execution: true,
        analytics: false,
        billing: false,
        settings: false,
        history: true
      }
    });
    setAssignModalOpen(true);
  };

  if (!canManageUsers()) {
    return (
      <Box p={3}>
        <Alert severity="error">
          You don't have permission to access user management.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <BreadcrumbsNav />
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">User Management</Typography>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => setInviteModalOpen(true)}
        >
          Invite User
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Active Users" />
          <Tab label="Pending Invitations" />
        </Tabs>
      </Paper>

      {/* Filters */}
      {tabValue === 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Search users"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Role</InputLabel>
                <Select
                  value={roleFilter}
                  label="Role"
                  onChange={(e) => setRoleFilter(e.target.value)}
                >
                  <MenuItem value="">All Roles</MenuItem>
                  {Object.entries(roleDisplayNames).map(([key, label]) => (
                    <MenuItem key={key} value={key}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setSearchTerm('');
                  setRoleFilter('');
                  setStatusFilter('');
                }}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Content */}
      {tabValue === 0 ? (
        <>
          {/* Users Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Companies</TableCell>
                  <TableCell>Projects</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user._id}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar>
                          {(user.firstName?.[0] || user.email[0]).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {user.firstName} {user.lastName}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {user.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={roleDisplayNames[user.role]}
                        color={roleColors[user.role]}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.isActivated && user.isActive ? 'Active' : 'Inactive'}
                        color={user.isActivated && user.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {user.assignedCompanies?.length || 0} companies
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {user.assignedProjects?.length || 0} projects
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {user.lastLogin 
                          ? new Date(user.lastLogin).toLocaleDateString()
                          : 'Never'
                        }
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => openEditModal(user)}
                        disabled={user._id === currentUser._id}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => openAssignModal(user)}
                      >
                        <AssignmentIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeactivateUser(user._id)}
                        disabled={user._id === currentUser._id}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, newPage) => setPage(newPage)}
            />
          </Box>
        </>
      ) : (
        /* Pending Invitations */
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Invited By</TableCell>
                <TableCell>Expires</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invitations.map((invitation) => (
                <TableRow key={invitation._id}>
                  <TableCell>{invitation.email}</TableCell>
                  <TableCell>
                    {invitation.firstName} {invitation.lastName}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={roleDisplayNames[invitation.role]}
                      color={roleColors[invitation.role]}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {invitation.invitedBy?.firstName} {invitation.invitedBy?.lastName}
                  </TableCell>
                  <TableCell>
                    {new Date(invitation.invitationExpires).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleResendInvitation(invitation._id)}
                      title="Resend invitation"
                    >
                      <RefreshIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleRevokeInvitation(invitation._id)}
                      color="error"
                      title="Revoke invitation"
                    >
                      <CancelIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Invite User Modal */}
      <Dialog open={inviteModalOpen} onClose={() => setInviteModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Invite New User</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Role</InputLabel>
                <Select
                  value={inviteForm.role}
                  label="Role"
                  onChange={(e) => setInviteForm(prev => ({ ...prev, role: e.target.value }))}
                >
                  {Object.entries(roleDisplayNames)
                    .filter(([key]) => {
                      // Super admin can invite anyone
                      if (hasRole('super_admin')) return true;
                      // Admin can invite manager and associate
                      if (hasRole('admin')) return ['manager', 'associate'].includes(key);
                      // Manager can invite associate only
                      if (hasRole('manager')) return key === 'associate';
                      return false;
                    })
                    .map(([key, label]) => (
                      <MenuItem key={key} value={key}>{label}</MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={inviteForm.firstName}
                onChange={(e) => setInviteForm(prev => ({ ...prev, firstName: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={inviteForm.lastName}
                onChange={(e) => setInviteForm(prev => ({ ...prev, lastName: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Custom Message (Optional)"
                multiline
                rows={3}
                value={inviteForm.invitationMessage}
                onChange={(e) => setInviteForm(prev => ({ ...prev, invitationMessage: e.target.value }))}
                helperText="This message will be included in the invitation email"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteModalOpen(false)}>Cancel</Button>
          <Button onClick={handleInviteUser} variant="contained" disabled={!inviteForm.email}>
            Send Invitation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={editForm.firstName}
                onChange={(e) => setEditForm(prev => ({ ...prev, firstName: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={editForm.lastName}
                onChange={(e) => setEditForm(prev => ({ ...prev, lastName: e.target.value }))}
              />
            </Grid>
            {hasRole('super_admin') && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={editForm.role}
                    label="Role"
                    onChange={(e) => setEditForm(prev => ({ ...prev, role: e.target.value }))}
                  >
                    {Object.entries(roleDisplayNames).map(([key, label]) => (
                      <MenuItem key={key} value={key}>{label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.isActive}
                    onChange={(e) => setEditForm(prev => ({ ...prev, isActive: e.target.checked }))}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditModalOpen(false)}>Cancel</Button>
          <Button onClick={handleEditUser} variant="contained">
            Update User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign User Modal */}
      <Dialog open={assignModalOpen} onClose={() => setAssignModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Assign User to {assignForm.type === 'company' ? 'Company' : 'Project'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Tabs 
              value={assignForm.type} 
              onChange={(e, newValue) => setAssignForm(prev => ({ ...prev, type: newValue }))}
            >
              <Tab label="Company" value="company" />
              <Tab label="Project" value="project" />
            </Tabs>

            <Box sx={{ mt: 3 }}>
              {assignForm.type === 'company' ? (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl fullWidth required>
                      <InputLabel>Company</InputLabel>
                      <Select
                        value={assignForm.companyId}
                        label="Company"
                        onChange={(e) => setAssignForm(prev => ({ ...prev, companyId: e.target.value }))}
                      >
                        {companies.map((company) => (
                          <MenuItem key={company._id} value={company._id}>
                            {company.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>Company Permissions</Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={assignForm.permissions.canManageProjects || false}
                          onChange={(e) => setAssignForm(prev => ({
                            ...prev,
                            permissions: { ...prev.permissions, canManageProjects: e.target.checked }
                          }))}
                        />
                      }
                      label="Can Manage Projects"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={assignForm.permissions.canViewAnalytics !== false}
                          onChange={(e) => setAssignForm(prev => ({
                            ...prev,
                            permissions: { ...prev.permissions, canViewAnalytics: e.target.checked }
                          }))}
                        />
                      }
                      label="Can View Analytics"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={assignForm.permissions.canManageBilling || false}
                          onChange={(e) => setAssignForm(prev => ({
                            ...prev,
                            permissions: { ...prev.permissions, canManageBilling: e.target.checked }
                          }))}
                        />
                      }
                      label="Can Manage Billing"
                    />
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Company</InputLabel>
                      <Select
                        value={assignForm.companyId}
                        label="Company"
                        onChange={(e) => setAssignForm(prev => ({ ...prev, companyId: e.target.value }))}
                      >
                        {companies.map((company) => (
                          <MenuItem key={company._id} value={company._id}>
                            {company.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Project</InputLabel>
                      <Select
                        value={assignForm.projectId}
                        label="Project"
                        onChange={(e) => setAssignForm(prev => ({ ...prev, projectId: e.target.value }))}
                        disabled={!assignForm.companyId}
                      >
                        {projects
                          .filter(project => project.companyId === assignForm.companyId)
                          .map((project) => (
                            <MenuItem key={project._id} value={project._id}>
                              {project.name}
                            </MenuItem>
                          ))
                        }
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>Section Access</Typography>
                    <Grid container spacing={1}>
                      {Object.entries({
                        flowDesigner: 'Flow Designer',
                        execution: 'Execution',
                        analytics: 'Analytics',
                        billing: 'Billing',
                        settings: 'Settings',
                        history: 'History'
                      }).map(([key, label]) => (
                        <Grid item xs={12} sm={6} key={key}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={assignForm.sectionAccess[key] || false}
                                onChange={(e) => setAssignForm(prev => ({
                                  ...prev,
                                  sectionAccess: { ...prev.sectionAccess, [key]: e.target.checked }
                                }))}
                              />
                            }
                            label={label}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignModalOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleAssignUser} 
            variant="contained"
            disabled={
              assignForm.type === 'company' ? !assignForm.companyId : 
              !assignForm.companyId || !assignForm.projectId
            }
          >
            Assign User
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagementPage;
